import { useParams, Link } from 'react-router-dom';
import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/navigation';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  const relatedArticles = useMemo(
    () => articles.filter((a) => a.slug !== slug).slice(0, 3),
    [slug]
  );

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const paragraphs = article.content.split('\n\n').filter(Boolean);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '文章', to: '/' },
              { label: article.title },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <header className="py-8 md:py-10">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-6">
              <span>{article.author}</span>
              <span>·</span>
              <span>{article.date}</span>
              <span>·</span>
              <span>阅读约{article.readTime}</span>
            </div>

            <div className="aspect-[21/9] rounded-xl overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <p className="text-base text-foreground-700 leading-relaxed bg-background-100 border-l-4 border-primary-500 pl-5 py-4 rounded-r-lg">
              {article.description}
            </p>
          </header>

          {/* Article Content */}
          <div className="prose-custom pb-10 md:pb-14">
            {paragraphs.map((para, idx) => {
              const trimmed = para.trim();
              if (!trimmed) return null;

              if (trimmed.startsWith('## ') && !trimmed.startsWith('### ')) {
                return (
                  <h2 key={idx} className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
                    {trimmed.replace('## ', '')}
                  </h2>
                );
              }

              if (trimmed.startsWith('### ')) {
                return (
                  <h3 key={idx} className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mt-8 mb-3">
                    {trimmed.replace('### ', '')}
                  </h3>
                );
              }

              if (trimmed.startsWith('- ')) {
                const items = trimmed.split('\n').map(line => line.replace('- ', '').trim());
                return (
                  <ul key={idx} className="list-disc list-inside space-y-1 mb-4 text-foreground-700 leading-relaxed">
                    {items.map((item, i) => (
                      <li key={i} className="text-sm md:text-base">{item}</li>
                    ))}
                  </ul>
                );
              }

              if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
                return (
                  <p key={idx} className="text-sm md:text-base font-semibold text-foreground-800 mt-6 mb-3">
                    {trimmed.replace(/\*\*/g, '')}
                  </p>
                );
              }

              if (trimmed.startsWith('1. ') || trimmed.startsWith('2. ') || trimmed.startsWith('3. ') || trimmed.startsWith('4. ') || trimmed.startsWith('5. ') || trimmed.startsWith('6. ')) {
                const items = trimmed.split('\n').map(line => {
                  const match = line.match(/^\d+\.\s+(.+)/);
                  return match ? match[1].trim() : line.trim();
                });
                return (
                  <ol key={idx} className="list-decimal list-inside space-y-2 mb-4 text-foreground-700 leading-relaxed">
                    {items.map((item, i) => (
                      <li key={i} className="text-sm md:text-base pl-1">
                        {item.replace(/\*\*(.+?)\*\*/g, '$1')}
                      </li>
                    ))}
                  </ol>
                );
              }

              return (
                <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {trimmed.replace(/\*\*(.+?)\*\*/g, '$1')}
                </p>
              );
            })}
          </div>

          <hr className="border-background-200/70 my-8" />

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="pb-14 md:pb-20">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 border border-background-200/70 hover:border-primary-200 rounded-xl overflow-hidden transition-all duration-200 cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-3 mb-2 text-xs text-foreground-500">
                        <span>{ra.date}</span>
                        <span>·</span>
                        <span>{ra.readTime}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.description,
            "author": { "@type": "Person", "name": article.author },
            "datePublished": article.date,
            "image": article.imageUrl
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.zhengpinlan.com" },
              { "@type": "ListItem", "position": 2, "name": article.title },
            ]
          })
        }}
      />
    </>
  );
}